import { SortCriteria, SortQuery, Task } from './types';
import { transformHourToDays } from '../../../views/ganttContainer/gantt/gantt.helper.js';
import { HOUR_TO_DAY_FIELDS } from './utils.ts';

export const buildSortQuery = (sortCriteria: SortCriteria[]): SortQuery => ({
  criteria: sortCriteria
});

export const createHierarchicalSortFunction =
  (sortQuery: SortQuery) =>
  (taskA: Task, taskB: Task): number => {
    // Don't sort root tasks (parent === 0) among themselves
    if (taskA.parent === 0 && taskB.parent === 0) {
      return 0;
    }

    // Don't sort tasks with different parents
    if (taskA.parent !== taskB.parent) {
      return 0;
    }

    // Apply multi-criteria sorting for tasks with the same parent
    return evaluateMultipleSortCriteria(taskA, taskB, sortQuery.criteria);
  };

export const createConstraintsSortFunction =
  (sortQuery: SortQuery) =>
  (taskA: Task, taskB: Task): number =>
    evaluateMultipleSortCriteria(taskA, taskB, sortQuery.criteria);

export const evaluateMultipleSortCriteria = (
  taskA: Task,
  taskB: Task,
  criteria: SortCriteria[]
): number => {
  for (const criterion of criteria) {
    const result = evaluateSingleSortCriterion(taskA, taskB, criterion);
    if (result !== 0) {
      return result;
    }
  }
  return 0;
};

export const evaluateSingleSortCriterion = (
  taskA: Task,
  taskB: Task,
  criterion: SortCriteria
): number => {
  const valueA = getTaskValueForSorting(taskA, criterion);
  const valueB = getTaskValueForSorting(taskB, criterion);
  const comparison = compareValues(valueA, valueB, criterion.data_type);

  return criterion.order_by === 'desc' ? -comparison : comparison;
};

const handleArrayFieldSorting = (
  rawValue: any,
  criterion: SortCriteria
): number => {
  if (!Array.isArray(rawValue)) {
    return 0;
  }

  const firstItem = criterion.el_to_extract_from
    ? rawValue.find((item) => item[criterion.el_to_extract_from])
    : rawValue[0];

  if (!firstItem) {
    return 0;
  }

  const extractedValue = criterion.el_to_extract_from
    ? firstItem[criterion.el_to_extract_from]
    : firstItem;

  const option = criterion.from_values?.find(
    ({ value }) => value === extractedValue
  );

  return option?.weigth ?? 0;
};

const handleUserFieldSorting = (rawValue: any): any => rawValue?.id ?? rawValue;

const handleHourToDayTransformation = (
  rawValue: any,
  criterionName: string
): any =>
  HOUR_TO_DAY_FIELDS.includes(criterionName) && rawValue
    ? transformHourToDays(rawValue)
    : rawValue;

export const getTaskValueForSorting = (
  task: Task,
  criterion: SortCriteria
): any => {
  const rawValue = task[criterion.name];

  // Handle array fields with weight mapping
  if (criterion.data_type?.includes('array/') && criterion.from_values) {
    return handleArrayFieldSorting(rawValue, criterion);
  }

  // Handle user field special case
  if (criterion.name === 'user') {
    return handleUserFieldSorting(rawValue);
  }

  // Transform hour-based fields to days if needed, otherwise return raw value
  return handleHourToDayTransformation(rawValue, criterion.name);
};

export const compareValues = (
  valueA: any,
  valueB: any,
  dataType: string
): number => {
  if (valueA === null && valueB === null) return 0;
  if (valueA === null) return -1;
  if (valueB === null) return 1;

  switch (dataType) {
    case 'date':
      return new Date(valueA).getTime() - new Date(valueB).getTime();
    case 'number':
      return Number(valueA) - Number(valueB);
    case 'string':
    default:
      return String(valueA)
        .toLowerCase()
        .localeCompare(String(valueB).toLowerCase());
  }
};
